/**
 * Fix for the recurring React error:
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed is not
 *    a child of this node."
 *
 * This crash is caused by external code mutating the DOM outside of React's
 * reconciler — most commonly:
 *   • Browser-level auto-translation (Google Translate on Chrome, Samsung
 *     Internet's auto-translate, Edge translator) which wraps every text node
 *     in <font> tags and then re-parents them. React still holds a reference
 *     to the original text node and tries to remove it from a parent that no
 *     longer owns it.
 *   • Some extensions (Grammarly, password managers, ad-blockers) doing the
 *     same kind of in-place DOM rewrite.
 *
 * Reference: https://github.com/facebook/react/issues/11538#issuecomment-417504600
 *
 * The patch below makes removeChild / insertBefore a no-op (instead of
 * throwing) when React tries to operate on a node that is no longer a child
 * of the expected parent. It only kicks in when the mismatch happens — normal
 * React rendering is untouched.
 */
if (typeof Node === "function" && Node.prototype) {
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function patchedRemoveChild(child) {
    if (child.parentNode !== this) {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn(
          "[Neobk.Pro] removeChild ignored — node was already detached (likely a browser translator)."
        );
      }
      return child;
    }
    return origRemoveChild.apply(this, arguments);
  };

  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function patchedInsertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn(
          "[Neobk.Pro] insertBefore ignored — reference node was already detached (likely a browser translator)."
        );
      }
      return newNode;
    }
    return origInsertBefore.apply(this, arguments);
  };
}
